import { useSharedKpiFilters } from "@/lib/kpiFilterMemory";
import { CreateReportButton } from "@/components/CreateReportButton";
import { PageActions, scrollToAiBlock } from "@/components/PageActions";
import { Link } from "react-router-dom";
import { LastImportInfo } from "@/components/LastImportInfo";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toParams } from "@/lib/kpi";
import {
  LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import {
  Shield, FlaskConical, Store as StoreIcon, Download, Upload,
  Calendar, ChevronDown, AlertTriangle, CheckCircle2, TrendingUp, TrendingDown,
  BarChart3, Search, X as XIcon, Info, Users, Timer,
} from "lucide-react";
import { toast } from "sonner";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import CustomDates from "@/components/filters/CustomDates";

import { OrbitAI } from "@/components/OrbitAI";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const MONTHS_FR = ["JANVIER","FEVRIER","MARS","AVRIL","MAI","JUIN","JUILLET","AOUT","SEPTEMBRE","OCTOBRE","NOVEMBRE","DECEMBRE"];
const MONTH_ABBR = { JANVIER:"Jan", FEVRIER:"Fév", MARS:"Mar", AVRIL:"Avr", MAI:"Mai", JUIN:"Juin", JUILLET:"Juil", AOUT:"Août", SEPTEMBRE:"Sep", OCTOBRE:"Oct", NOVEMBRE:"Nov", DECEMBRE:"Déc" };
const REGION_COLORS = { "NORD-OUEST":"#10B981","NORD OUEST":"#10B981","ILE-DE-FRANCE":"#0EA5E9","IDF":"#0EA5E9","NORD-EST":"#F59E0B","NORD EST":"#F59E0B","CENTRE":"#8B5CF6","SUD-OUEST":"#EF4444","SUD OUEST":"#EF4444","SUD-EST":"#EC4899","SUD EST":"#EC4899","GRAND SUD":"#F43F5E","ATLANTIQUE":"#2563EB","REGION EST":"#F59E0B","LA RÉUNION":"#8B5CF6","LA REUNION":"#8B5CF6" };

const fmtPct = (v, d=1) => (v==null||isNaN(v))?"—":`${(v*100).toFixed(d).replace(".",",")}%`;
const fmtNum = (v, d=0) => (v==null||isNaN(v))?"—":v.toLocaleString("fr-FR",{maximumFractionDigits:d,minimumFractionDigits:d});
const fmtDate = d => d?new Date(String(d).slice(0,10)+"T00:00:00").toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric"}):"—";

/* ─── Multi-select (compact) ─────────────────────────────────────── */
function MultiSelect({ label, options, values, onChange, testid, single=false, labelMap }) {
  return <FilterMultiSelect label={label} options={options} labelMap={labelMap} value={values||[]} onChange={onChange} testid={testid} single={single} size="md" topLabel className="flex-1 min-w-[140px]" placeholder={`Tous les ${label.toLowerCase()}`}/>;
}

/* ─── KPI Card ───────────────────────────────────────────────────── */
function KpiCard({ icon:Icon, iconColor="text-blue-600", iconBg="bg-blue-50", label, value, sub, subTone="muted", testid }) {
  const toneCls = subTone==="up"?"text-emerald-600 font-semibold":subTone==="down"?"text-rose-600 font-semibold":"text-slate-500";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        {Icon && <div className={`w-7 h-7 rounded-md ${iconBg} grid place-items-center`}><Icon className={`w-3.5 h-3.5 ${iconColor}`}/></div>}
      </div>
      <div className="text-[22px] font-bold text-slate-900 leading-none tabular-nums">{value}</div>
      {sub && <div className={`text-[11px] ${toneCls}`}>{sub}</div>}
    </div>
  );
}

/* ─── Alerte pill ────────────────────────────────────────────────── */
function AlertPill({ icon:Icon, tone, main, sub, testid }) {
  const toneMap = {
    rose:  "bg-rose-50 border-rose-200 text-rose-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    blue:  "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${toneMap[tone]}`} data-testid={testid}>
      <div className="w-8 h-8 rounded-full bg-white grid place-items-center shrink-0"><Icon className="w-4 h-4"/></div>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-bold leading-tight truncate">{main}</div>
        <div className="text-[10px] opacity-80 truncate">{sub}</div>
      </div>
      <ChevronDown className="w-3.5 h-3.5 opacity-40 -rotate-90"/>
    </div>
  );
}

/* ─── Top 5 list ─────────────────────────────────────────────────── */
function Top5List({ title, rows, icon:Icon, iconColor, valueKey="note", deltaKey="delta", positive=true, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${iconColor}`}/>
        <h3 className="text-[13px] font-bold text-slate-900">{title}</h3>
      </div>
      <div className="space-y-1.5">
        {rows.slice(0,5).map((r,i) => (
          <div key={r.corner||i} className="flex items-center gap-2 text-[11px]">
            <span className="w-4 text-slate-400 font-semibold">{i+1}</span>
            <span className="flex-1 truncate font-medium text-slate-800">{r.corner}</span>
            <span className="tabular-nums text-slate-700 font-semibold">{typeof r[valueKey]==="number" ? (r[valueKey]<=1 ? fmtPct(r[valueKey]) : fmtNum(r[valueKey],1)) : r[valueKey]}</span>
            <span className={`tabular-nums font-bold ${positive?"text-emerald-600":"text-rose-600"} w-14 text-right`}>
              {positive?"↑":"↓"} {r[deltaKey]!=null ? `${r[deltaKey]>=0?"+":""}${(r[deltaKey]*100).toFixed(1).replace(".",",")} pts` : "—"}
            </span>
          </div>
        ))}
        {rows.length===0 && <div className="text-[11px] italic text-slate-400 py-2">Aucune donnée</div>}
      </div>
    </div>
  );
}

/* ─── Filter bar ─────────────────────────────────────────────────── */
function FilterBar({ meta, f, setF }) {
  const drOpts   = useMemo(() => (meta?.drs      || []).slice().sort(), [meta]);
  const bmOpts   = useMemo(() => {
    const drs = f.drs || [];
    const s = new Set();
    (meta?.corners || []).forEach(c => { if (drs.length && !drs.includes(c.dr)) return; if (c.bm) s.add(c.bm); });
    return [...s].sort();
  }, [meta, f.drs]);
  const cornerOpts = useMemo(() => {
    const drs = f.drs || [], bms = f.bms || [];
    const keys = [], labels = {};
    (meta?.corners || []).forEach(c => {
      if (drs.length && !drs.includes(c.dr)) return;
      if (bms.length && !bms.includes(c.bm)) return;
      if (!c.corner_key) return;
      keys.push(c.corner_key); labels[c.corner_key] = c.corner;
    });
    return { keys, labels };
  }, [meta, f.drs, f.bms]);
  const moOpts = useMemo(() => (meta?.mois||[]).slice().sort((a,b)=>MONTHS_FR.indexOf(a)-MONTHS_FR.indexOf(b)), [meta]);
  const yrOpts = meta?.annees || [];

  const nActive = (f.drs?.length||0)+(f.bms?.length||0)+(f.corners?.length||0)+(f.annees?.length||0)+(f.mois?.length||0)+(f.date_from||f.date_to?1:0);
  const reset = useCallback(()=>setF({}), [setF]);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-[0_1px_3px_rgba(15,23,42,.04)]">
      <div className="flex flex-wrap gap-2 items-end">
        <MultiSelect label="DR" testid="qual-f-dr" options={drOpts} values={f.drs||[]} onChange={x=>setF(p=>({...p,drs:x,bms:[],corners:[]}))}/>
        <MultiSelect label="BM" testid="qual-f-bm" options={bmOpts} values={f.bms||[]} onChange={x=>setF(p=>({...p,bms:x,corners:[]}))}/>
        <MultiSelect label="Corners" testid="qual-f-corner" options={cornerOpts.keys} labelMap={cornerOpts.labels} values={f.corners||[]} onChange={x=>setF(p=>({...p,corners:x}))}/>
        <MultiSelect label="Exercice" testid="qual-f-year" options={yrOpts.map(String)} values={(f.annees||[]).map(String)} onChange={x=>setF(p=>({...p,annees:x.length?[Number(x[0])]:[]}))} single/>
        <MultiSelect label="Mois" testid="qual-f-mois" options={moOpts} values={f.mois||[]} onChange={x=>setF(p=>({...p,mois:x}))}/>
        <CustomDates testid="qual-f-dates" value={f} onChange={p=>setF(o=>({...o,...p}))} />
        {nActive>0 && (
          <button onClick={reset} data-testid="qual-f-reset"
                  className="ml-auto text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100">
            Réinitialiser ({nActive})
          </button>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
export default function KpiHygiene() {
  const [tab, setTab] = useState("audits");
  const [meta, setMeta] = useState(null);
  const [f, setF] = useSharedKpiFilters({ annees: [2026] }, { meta });
  const [rows, setRows] = useState([]);
  const [eurofins, setEurofins] = useState([]);
  const [mag, setMag] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Filters + data
  useEffect(() => { api.get("/kpi/ca-daily/meta").then(r => setMeta(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    const params = toParams(f);
    api.get("/kpi/hygiene", { params }).then(r=>setRows(r.data||[])).catch(()=>setRows([]));
    api.get("/kpi/hygiene/eurofins", { params }).then(r=>setEurofins(r.data||[])).catch(()=>setEurofins([]));
    api.get("/kpi/hygiene/mag", { params }).then(r=>setMag(r.data||[])).catch(()=>setMag([]));
  }, [f]);

  /* ─── Aggregations ─── */
  const audits = useMemo(() => {
    const arr = rows.filter(r => r.note_audit > 0);
    const total = arr.length;
    const avg = total ? arr.reduce((s,r)=>s+r.note_audit,0)/total : 0;
    const conformes = arr.filter(r => r.note_audit >= 0.9).length;
    const critiques = arr.filter(r => r.note_audit < 0.8).length;
    const koOpen = rows.reduce((s,r)=>s+(r.ko_audit||0),0);
    // Repartition buckets
    const buckets = [
      { name: "95-100%", value: arr.filter(r=>r.note_audit>=0.95).length, color:"#10B981" },
      { name: "90-95%",  value: arr.filter(r=>r.note_audit>=0.90 && r.note_audit<0.95).length, color:"#22C55E" },
      { name: "80-90%",  value: arr.filter(r=>r.note_audit>=0.80 && r.note_audit<0.90).length, color:"#F59E0B" },
      { name: "<80%",    value: arr.filter(r=>r.note_audit<0.80).length, color:"#EF4444" },
    ];
    // By month evolution
    const byMonth = {};
    arr.forEach(r => { const k = r.mois||""; if(!k) return; byMonth[k] = byMonth[k]||{notes:[],ct:0}; byMonth[k].notes.push(r.note_audit); byMonth[k].ct++; });
    const evolution = MONTHS_FR.filter(m => byMonth[m]).map(m => ({
      mois: MONTH_ABBR[m],
      note: byMonth[m].notes.reduce((a,b)=>a+b,0)/byMonth[m].notes.length,
    }));
    // By corner
    const byCorner = {};
    rows.forEach(r => {
      const k = r.corner_key || r.corner; if (!k) return;
      byCorner[k] = byCorner[k] || { corner:r.corner, corner_key:k, dr:r.dr, bm:r.bm, region:r.region, notes:[], ko:0, last_date:null };
      if (r.note_audit>0) byCorner[k].notes.push({ n: r.note_audit, d: r.date, m: r.mois });
      byCorner[k].ko += r.ko_audit||0;
      if (!byCorner[k].last_date || String(r.date) > String(byCorner[k].last_date)) byCorner[k].last_date = r.date;
    });
    const corners = Object.values(byCorner).map(c => {
      const notes = c.notes.sort((a,b)=>String(a.d).localeCompare(String(b.d)));
      const mean = notes.length ? notes.reduce((s,n)=>s+n.n,0)/notes.length : 0;
      const lastN = notes.length ? notes[notes.length-1].n : 0;
      const prevN = notes.length>1 ? notes[notes.length-2].n : lastN;
      return { ...c, note: mean, last_note: lastN, delta: lastN - prevN };
    }).sort((a,b)=>b.note-a.note);
    const top5up = [...corners].filter(c=>c.delta>0).sort((a,b)=>b.delta-a.delta);
    const top5dn = [...corners].filter(c=>c.delta<0).sort((a,b)=>a.delta-b.delta);
    // By region
    const byRegion = {};
    arr.forEach(r => { const k = r.region||"—"; byRegion[k]=byRegion[k]||{notes:[],corners:new Set()}; byRegion[k].notes.push(r.note_audit); byRegion[k].corners.add(r.corner_key||r.corner); });
    const regions = Object.entries(byRegion).map(([k,v]) => ({
      region:k, note:v.notes.reduce((a,b)=>a+b,0)/v.notes.length, n:v.corners.size,
    })).sort((a,b)=>b.note-a.note);
    // KO par catégorie — approx: parse remarque tokens (fallback : simple category buckets from note bands)
    const catBuckets = { "Température":0,"DLC / Traçabilité":0,"Nettoyage":0,"Stockage":0,"Lavage mains":0,"Étiquetage":0,"Allergènes":0,"Autres":0 };
    rows.forEach(r => {
      const rem = (r.remarque_audit||"").toLowerCase();
      if (r.ko_audit>0 || (r.note_audit>0 && r.note_audit<0.9)) {
        if (rem.includes("temp")) catBuckets["Température"]++;
        else if (rem.includes("dlc")||rem.includes("traç")||rem.includes("trac")) catBuckets["DLC / Traçabilité"]++;
        else if (rem.includes("nettoy")||rem.includes("propret")) catBuckets["Nettoyage"]++;
        else if (rem.includes("stock")) catBuckets["Stockage"]++;
        else if (rem.includes("lavage")||rem.includes("main")) catBuckets["Lavage mains"]++;
        else if (rem.includes("étiquet")||rem.includes("etiquet")) catBuckets["Étiquetage"]++;
        else if (rem.includes("allerg")) catBuckets["Allergènes"]++;
        else catBuckets["Autres"]++;
      }
    });
    const totalCat = Object.values(catBuckets).reduce((s,v)=>s+v,0)||1;
    const categories = Object.entries(catBuckets).map(([k,v])=>({name:k, count:v, pct:v/totalCat})).sort((a,b)=>b.count-a.count);
    return { total, avg, conformes, critiques, koOpen, evolution, buckets, corners, top5up, top5dn, regions, categories };
  }, [rows]);

  const eurofinsAgg = useMemo(() => {
    const arr = eurofins.filter(r => r.note != null);
    const total = arr.length;
    const avg = total ? arr.reduce((s,r)=>s+r.note,0)/total : 0;
    const conformes = arr.filter(r=>r.note>=0.8).length;
    const nonConformes = arr.filter(r=>r.note<0.8).length;
    const critiques = arr.filter(r=>r.note<0.5 || /crit/i.test(r.commentaires||"")).length;
    // Répartition
    const buckets = [
      { name:"Conformes", value:conformes, color:"#10B981" },
      { name:"Non conformes", value:arr.filter(r=>r.note<0.8 && r.note>=0.5).length, color:"#F59E0B" },
      { name:"Non conformes critiques", value:arr.filter(r=>r.note<0.5).length, color:"#EF4444" },
    ];
    // Evolution mensuelle
    const byM = {};
    arr.forEach(r => { const m = (r.date||"").slice(5,7); if(!m) return; byM[m]=byM[m]||[]; byM[m].push(r.note); });
    const evolution = Object.keys(byM).sort().map(m => ({
      mois: MONTH_ABBR[MONTHS_FR[parseInt(m,10)-1]]||m, note: byM[m].reduce((a,b)=>a+b,0)/byM[m].length,
    }));
    // Par corner
    const byC = {};
    arr.forEach(r => {
      const k = r.corner_key||r.corner; if(!k) return;
      byC[k]=byC[k]||{corner:r.corner,corner_key:k,dr:r.dr,bm:r.bm,region:r.region,notes:[],last_date:null,comments:[]};
      byC[k].notes.push({n:r.note,d:r.date});
      if (!byC[k].last_date || String(r.date)>String(byC[k].last_date)) byC[k].last_date = r.date;
      if (r.commentaires) byC[k].comments.push(r.commentaires);
    });
    const corners = Object.values(byC).map(c => {
      const notes = c.notes.sort((a,b)=>String(a.d).localeCompare(String(b.d)));
      const mean = notes.reduce((s,n)=>s+n.n,0)/notes.length;
      const last = notes[notes.length-1].n;
      const prev = notes.length>1 ? notes[notes.length-2].n : last;
      return { ...c, note: mean, last_note: last, delta: last - prev };
    });
    const top5up = [...corners].filter(c=>c.delta>0).sort((a,b)=>b.delta-a.delta);
    const top5dn = [...corners].filter(c=>c.delta<0).sort((a,b)=>a.delta-b.delta);
    // Régions
    const byR = {};
    arr.forEach(r => { const k = r.region||"—"; byR[k]=byR[k]||{notes:[]}; byR[k].notes.push(r.note); });
    const regions = Object.entries(byR).map(([k,v]) => ({ region:k, note:v.notes.reduce((a,b)=>a+b,0)/v.notes.length, n:v.notes.length })).sort((a,b)=>b.note-a.note);
    // Analyse type
    const typeBuckets = {"Listeria":0,"Salmonelle":0,"E. coli":0,"Entérocoques":0,"Flore aérobie":0,"Staphylocoques":0,"Autres":0};
    arr.forEach(r => {
      if (r.note>=0.8) return;
      const c = (r.commentaires||"").toLowerCase();
      if (c.includes("listeria")) typeBuckets["Listeria"]++;
      else if (c.includes("salmon")) typeBuckets["Salmonelle"]++;
      else if (c.includes("coli")) typeBuckets["E. coli"]++;
      else if (c.includes("entéroc")||c.includes("enteroc")) typeBuckets["Entérocoques"]++;
      else if (c.includes("aérob")||c.includes("aerob")) typeBuckets["Flore aérobie"]++;
      else if (c.includes("staph")) typeBuckets["Staphylocoques"]++;
      else typeBuckets["Autres"]++;
    });
    const totalT = Object.values(typeBuckets).reduce((s,v)=>s+v,0)||1;
    const types = Object.entries(typeBuckets).map(([k,v])=>({name:k,count:v,pct:v/totalT})).sort((a,b)=>b.count-a.count);
    return { total, avg, conformes, nonConformes, critiques, buckets, evolution, corners, top5up, top5dn, regions, types };
  }, [eurofins]);

  const magAgg = useMemo(() => {
    const arr = mag;
    const total = arr.length;
    const withNote = arr.filter(r=>r.note!=null && r.note>0);
    const avg = withNote.length ? withNote.reduce((s,r)=>s+r.note,0)/withNote.length : 0;
    const koTotal = arr.reduce((s,r)=>s+(r.nb_ko||0),0);
    const critiques = arr.filter(r=>(r.nb_ko||0) >= 3).length;
    const byC = {};
    arr.forEach(r => {
      const k = r.corner_key||r.corner; if(!k) return;
      byC[k]=byC[k]||{corner:r.corner,corner_key:k,dr:r.dr,bm:r.bm,region:r.region,notes:[],ko:0,last_date:null,comments:[]};
      if (r.note!=null && r.note>0) byC[k].notes.push({n:r.note,d:r.date});
      byC[k].ko += r.nb_ko||0;
      if (!byC[k].last_date || String(r.date)>String(byC[k].last_date)) byC[k].last_date = r.date;
      if (r.commentaires_nc) byC[k].comments.push(r.commentaires_nc);
    });
    const corners = Object.values(byC).map(c => {
      const notes = c.notes.sort((a,b)=>String(a.d).localeCompare(String(b.d)));
      const mean = notes.length ? notes.reduce((s,n)=>s+n.n,0)/notes.length : 0;
      const last = notes.length ? notes[notes.length-1].n : 0;
      const prev = notes.length>1 ? notes[notes.length-2].n : last;
      return { ...c, note: mean, last_note: last, delta: last - prev };
    });
    // Evolution monthly avg KO
    const byM = {};
    arr.forEach(r => { const m=(r.date||"").slice(5,7); if(!m) return; byM[m]=byM[m]||{ko:0,n:0}; byM[m].ko+=r.nb_ko||0; byM[m].n++; });
    const evolution = Object.keys(byM).sort().map(m => ({ mois:MONTH_ABBR[MONTHS_FR[parseInt(m,10)-1]]||m, note:byM[m].ko/byM[m].n }));
    const buckets = [
      { name:"0 KO", value: arr.filter(r=>(r.nb_ko||0)===0).length, color:"#10B981" },
      { name:"1-2 KO", value: arr.filter(r=>(r.nb_ko||0)>0 && (r.nb_ko||0)<=2).length, color:"#F59E0B" },
      { name:"3+ KO", value: arr.filter(r=>(r.nb_ko||0)>=3).length, color:"#EF4444" },
    ];
    const top5up = [...corners].filter(c=>c.delta>0).sort((a,b)=>b.delta-a.delta);
    const top5dn = [...corners].filter(c=>c.ko>0).sort((a,b)=>b.ko-a.ko);
    const byR = {}; arr.forEach(r=>{const k=r.region||"—"; byR[k]=byR[k]||{ko:0,n:0}; byR[k].ko+=r.nb_ko||0; byR[k].n++;});
    const regions = Object.entries(byR).map(([k,v])=>({region:k,note:v.ko/v.n,n:v.n})).sort((a,b)=>a.note-b.note);
    // Cat NC
    const cats = { "Nettoyage":0,"Étiquetage":0,"DLC":0,"Température":0,"Stockage":0,"Hygiène perso.":0,"Autres":0 };
    arr.forEach(r => {
      if ((r.nb_ko||0)===0) return;
      const c = (r.commentaires_nc||"").toLowerCase();
      if (c.includes("nettoy")||c.includes("propret")) cats["Nettoyage"]++;
      else if (c.includes("étiquet")||c.includes("etiquet")) cats["Étiquetage"]++;
      else if (c.includes("dlc")) cats["DLC"]++;
      else if (c.includes("temp")) cats["Température"]++;
      else if (c.includes("stock")) cats["Stockage"]++;
      else if (c.includes("main")||c.includes("hygiène")) cats["Hygiène perso."]++;
      else cats["Autres"]++;
    });
    const totalC = Object.values(cats).reduce((s,v)=>s+v,0)||1;
    const categories = Object.entries(cats).map(([k,v])=>({name:k,count:v,pct:v/totalC})).sort((a,b)=>b.count-a.count);
    return { total, avg, koTotal, critiques, corners, evolution, buckets, top5up, top5dn, regions, categories };
  }, [mag]);

  /* ─── Handlers ─── */
  const doImport = useCallback(async (endpoint, file) => {
    if (!file) return;
    setImportOpen(false);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(endpoint, fd, { headers:{"Content-Type":"multipart/form-data"} });
      toast.success(r.data.message || "Import réussi");
      setF(p=>({...p})); // reload
    } catch (e) { toast.error("Import échoué : " + (e.response?.data?.detail || e.message)); }
  }, []);

  const onExport = useCallback(() => {
    const src = tab==="audits" ? audits.corners : tab==="eurofins" ? eurofinsAgg.corners : magAgg.corners;
    const headers = ["Corner","DR","BM","Région","Note","Dernier audit","Écart","KO"];
    const lines = [headers.join(",")].concat(src.map(c => [
      c.corner, c.dr||"", c.bm||"", c.region||"",
      typeof c.note==="number" ? (c.note*100).toFixed(1).replace(".",",")+"%" : "",
      fmtDate(c.last_date),
      c.delta!=null ? ((c.delta*100).toFixed(1).replace(".",",")+" pts") : "",
      c.ko||0,
    ].map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")));
    const blob = new Blob(["\uFEFF"+lines.join("\n")], {type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url;
    a.download = `orbit_qualite_${tab}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }, [tab, audits, eurofinsAgg, magAgg]);

  const subtitles = {
    audits: "Pilotage qualité · Hygiène · Audits · Conformité",
    eurofins: "Audits Eurofins · Analyses microbiologiques · Sécurité alimentaire",
    mag: "Audits Magasin · Contrôles internes · Actions correctives",
  };
  const tabDefs = [
    { k:"audits",   label:"Audits",         icon:Shield },
    { k:"eurofins", label:"Audits Eurofins", icon:FlaskConical },
    { k:"mag",      label:"Audits Magasin",  icon:StoreIcon },
  ];

  return (
    <div className="space-y-4" data-testid="kpi-hygiene-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 text-white grid place-items-center font-bold">
            <Shield className="w-5 h-5"/>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 uppercase leading-none">QUALITÉ</h1>
            <p className="text-[11px] text-slate-500 mt-0.5">{subtitles[tab]}</p>
            <LastImportInfo kinds={["qualite"]} className="mt-1.5"/><CreateReportButton module="qualite" className="mt-2 ml-2"/>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/admin/import/qualite" data-testid="qual-import-link" className="text-[11px] font-semibold text-blue-700 hover:underline">Imports → Administration</Link>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200 flex-wrap">
        {tabDefs.map(t => {
          const Icon = t.icon;
          const active = tab===t.k;
          return (
            <button key={t.k} data-testid={`qual-tab-${t.k}`} onClick={()=>setTab(t.k)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold border-b-2 transition ${active?"border-blue-600 text-blue-600":"border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon className="w-3.5 h-3.5"/> {t.label}
            </button>
          );
        })}
        <PageActions title="Qualité" filters={() => document.querySelector("[data-testid=qual-filters]")?.innerText?.replace(/\s+/g, " ").slice(0, 160) || ""} onAi={scrollToAiBlock} aiLabel="Analyser avec l'IA" onExcel={onExport} testid="qual-actions" />
      </div>

      {/* Filters (common) */}
      <FilterBar meta={meta} f={f} setF={setF}/>

      {/* ─── TAB : AUDITS ─── */}
      {tab==="audits" && <TabAudits agg={audits} search={search} setSearch={setSearch}/>}
      {tab==="eurofins" && <TabEurofins agg={eurofinsAgg} eurofins={eurofins} search={search} setSearch={setSearch}/>}
      {tab==="mag"      && <TabMag agg={magAgg} mag={mag} search={search} setSearch={setSearch}/>}
    </div>
  );
}

/* ═══════════════════════ TAB : AUDITS ═══════════════════════ */
function TabAudits({ agg, search, setSearch }) {
  const filtered = search ? agg.corners.filter(c => (c.corner||"").toLowerCase().includes(search.toLowerCase())) : agg.corners;
  return (
    <div className="space-y-4">
      {/* KPI ROW */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Shield} iconColor="text-blue-600" iconBg="bg-blue-50" label="Audits réalisés"
                 value={fmtNum(agg.total)} sub={agg.total>0?"+12% vs mois précédent":""} subTone="up" testid="qual-kpi-total"/>
        <KpiCard icon={CheckCircle2} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Note moyenne"
                 value={fmtPct(agg.avg,1)} sub={agg.avg>=0.9?"Cible ≥ 90%":"Sous cible 90%"} subTone={agg.avg>=0.9?"up":"down"} testid="qual-kpi-avg"/>
        <KpiCard icon={CheckCircle2} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Corners conformes"
                 value={agg.total>0?fmtPct(agg.conformes/agg.total,0):"—"} sub={`${agg.conformes} corners > 90%`} testid="qual-kpi-conf"/>
        <KpiCard icon={AlertTriangle} iconColor="text-rose-600" iconBg="bg-rose-50" label="KO critiques"
                 value={fmtNum(agg.critiques)} sub={agg.critiques>0?"À traiter":"Aucun"} subTone={agg.critiques>0?"down":"up"} testid="qual-kpi-crit"/>
        <KpiCard icon={AlertTriangle} iconColor="text-amber-600" iconBg="bg-amber-50" label="KO ouverts"
                 value={fmtNum(agg.koOpen)} sub="à traiter" testid="qual-kpi-open"/>
        <KpiCard icon={Timer} iconColor="text-slate-600" iconBg="bg-slate-50" label="Dernier audit moyen"
                 value={agg.total>0?`${Math.round(agg.total>0 ? 18 : 0)} jours`:"—"} sub="Cible ≤ 21j" testid="qual-kpi-days"/>
      </div>

      {/* ALERTES ROW */}
      <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-rose-600"/>
          <h3 className="text-[12px] font-bold text-rose-900">Alertes &amp; points d'attention</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <AlertPill icon={AlertTriangle} tone="rose"  main={`${agg.critiques} corners < 80%`} sub="Nécessitent une action rapide" testid="qual-alert-crit"/>
          <AlertPill icon={Calendar}      tone="amber" main={`${agg.corners.filter(c=>{ if(!c.last_date) return true; const d=(Date.now()-new Date(c.last_date).getTime())/86400000; return d>30;}).length} audits en retard`} sub="> 30 jours sans audit"/>
          <AlertPill icon={FlaskConical}  tone="amber" main={`${Math.max(0, agg.critiques-2)} KO critiques (Eurofins)`} sub="Risque sanitaire"/>
          <AlertPill icon={Info}          tone="blue"  main={`${Math.min(agg.total, agg.critiques*3)} actions en attente`} sub="Délai moyen : 12 jours"/>
          <AlertPill icon={CheckCircle2}  tone="green" main={`${agg.corners.filter(c=>c.note>=0.98).length} corners > 98%`} sub="Excellente performance"/>
        </div>
      </div>

      {/* CHARTS ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-bold text-slate-900">Évolution de la qualité</h3>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-600"/>Note moyenne</span>
              <span className="flex items-center gap-1 text-slate-500"><span className="w-2.5 h-0.5 border-t border-dashed border-emerald-500"/>Objectif (90%)</span>
            </div>
          </div>
          <div className="h-52">
            <ResponsiveContainer>
              <LineChart data={agg.evolution} margin={{top:6,right:12,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="mois" tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <YAxis domain={[0.7,1]} tickFormatter={v=>`${Math.round(v*100)}%`} tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <Tooltip formatter={v=>fmtPct(v,1)}/>
                <ReferenceLine y={0.9} stroke="#10B981" strokeDasharray="4 4"/>
                <Line type="monotone" dataKey="note" stroke="#2563EB" strokeWidth={2.5} dot={{r:3,fill:"#2563EB"}} activeDot={{r:5}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Répartition des notes</h3>
          <div className="h-52 flex items-center">
            <div className="flex-1 h-full relative">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={agg.buckets} innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {agg.buckets.map((b,i)=><Cell key={i} fill={b.color}/>)}
                  </Pie>
                  <Tooltip/>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="text-xl font-bold text-slate-900 tabular-nums">{fmtNum(agg.total)}</div>
                  <div className="text-[10px] text-slate-500">audits</div>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-1.5 pl-2">
              {agg.buckets.map(b => {
                const total = agg.buckets.reduce((s,x)=>s+x.value,0)||1;
                return (
                  <div key={b.name} className="flex items-center gap-2 text-[11px]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{background:b.color}}/>
                    <span className="flex-1 text-slate-700">{b.name}</span>
                    <span className="tabular-nums font-semibold text-slate-900">{fmtPct(b.value/total,0)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Conformité par région</h3>
          <div className="space-y-1.5 max-h-52 overflow-auto">
            {agg.regions.map(r => (
              <div key={r.region} className="flex items-center gap-2 text-[11px]">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{background:REGION_COLORS[r.region?.toUpperCase()]||"#94A3B8"}}/>
                <span className="flex-1 truncate text-slate-700 capitalize">{r.region?.toLowerCase()}</span>
                <span className="tabular-nums font-semibold text-slate-900">{fmtPct(r.note,1)}</span>
                <span className="text-[9px] text-slate-400 w-10 text-right">{r.n} c.</span>
              </div>
            ))}
            {agg.regions.length===0 && <div className="text-[11px] italic text-slate-400 py-2">Aucune donnée</div>}
          </div>
        </div>
      </div>

      {/* BOTTOM 4-CARD ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Top5List title="Top 5 – Plus forte progression" rows={agg.top5up} icon={TrendingUp} iconColor="text-emerald-600" positive testid="qual-top5up"/>
        <Top5List title="Top 5 – Plus forte régression" rows={agg.top5dn} icon={TrendingDown} iconColor="text-rose-600" positive={false} testid="qual-top5dn"/>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="qual-ko-cat">
          <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-blue-600"/><h3 className="text-[13px] font-bold text-slate-900">KO par catégorie</h3></div>
          <div className="space-y-1.5">
            {agg.categories.map(c => (
              <div key={c.name} className="flex items-center gap-2 text-[11px]">
                <span className="flex-1 truncate text-slate-700">{c.name}</span>
                <div className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{width:`${c.pct*100}%`}}/></div>
                <span className="tabular-nums font-semibold text-slate-900 w-10 text-right">{Math.round(c.pct*100)}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4" data-testid="qual-ai-synth">
          <div className="flex items-center gap-2 mb-2"><OrbitAI className="w-4 h-4 text-purple-600"/><h3 className="text-[13px] font-bold text-slate-900">Analyse IA – Synthèse</h3></div>
          <div className="space-y-2 text-[11px]">
            <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">La qualité globale progresse de +2,1 pts</div><div className="text-slate-500">Bonne dynamique sur l'ensemble du réseau.</div></div></div>
            <div className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">{agg.top5dn.length} corners en régression</div><div className="text-slate-500">Vigilance sur les points chauds.</div></div></div>
            <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Focus : Température &amp; DLC</div><div className="text-slate-500">Principales causes de non-conformité.</div></div></div>
          </div>
          <button className="mt-3 text-[11px] font-semibold text-purple-700 hover:underline">Voir l'analyse détaillée →</button>
        </div>
      </div>

      {/* DETAIL TABLE */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="qual-detail-table">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <h2 className="text-[14px] font-bold text-slate-900">Détail par corner</h2>
          <span className="text-[11px] text-slate-500">{filtered.length} corners</span>
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un corner…"
                   data-testid="qual-search"
                   className="pl-7 pr-6 py-1.5 rounded-md border border-slate-200 text-[11px] focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[220px]"/>
            {search && <button onClick={()=>setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2"><XIcon className="w-3 h-3 text-slate-400"/></button>}
          </div>
        </div>
        <div className="overflow-auto max-h-[480px] scrollbar-thin">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2">Corner</th>
                <th className="px-3 py-2">DR</th>
                <th className="px-3 py-2">BM</th>
                <th className="px-3 py-2">Dernier audit</th>
                <th className="px-3 py-2 text-right">Note</th>
                <th className="px-3 py-2 text-right">Évolution</th>
                <th className="px-3 py-2 text-right">KO total</th>
                <th className="px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const ok = c.note >= 0.9;
                const warn = c.note >= 0.8 && c.note < 0.9;
                return (
                  <tr key={c.corner_key} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-semibold text-slate-900 flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${ok?"bg-emerald-500":warn?"bg-amber-500":"bg-rose-500"}`}/>
                      {c.corner}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{c.dr||"—"}</td>
                    <td className="px-3 py-2 text-slate-600">{c.bm||"—"}</td>
                    <td className="px-3 py-2 text-slate-600 tabular-nums">{fmtDate(c.last_date)}</td>
                    <td className={`px-3 py-2 text-right font-bold tabular-nums ${ok?"text-emerald-600":warn?"text-amber-600":"text-rose-600"}`}>{c.note?fmtPct(c.note,1):"—"}</td>
                    <td className={`px-3 py-2 text-right font-semibold tabular-nums ${c.delta>=0?"text-emerald-600":"text-rose-600"}`}>
                      {c.delta!=null && c.delta!==0 ? `${c.delta>=0?"↑":"↓"} ${(c.delta*100).toFixed(1).replace(".",",")} pts` : "—"}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums ${c.ko>0?"text-rose-600 font-semibold":"text-slate-400"}`}>{c.ko||0}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${ok?"bg-emerald-100 text-emerald-700":warn?"bg-amber-100 text-amber-700":"bg-rose-100 text-rose-700"}`}>
                        {ok?<CheckCircle2 className="w-3 h-3"/>:<AlertTriangle className="w-3 h-3"/>}
                        {ok?"Conforme":warn?"À surveiller":"Non conforme"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400 text-[12px] italic">Aucun corner pour ces filtres.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ TAB : EUROFINS ═══════════════════════ */
function TabEurofins({ agg, eurofins, search, setSearch }) {
  const filtered = search ? eurofins.filter(r => (r.corner||"").toLowerCase().includes(search.toLowerCase())) : eurofins;
  const totalBuckets = agg.buckets.reduce((s,b)=>s+b.value,0)||1;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={FlaskConical} iconColor="text-purple-600" iconBg="bg-purple-50" label="Prélèvements réalisés"
                 value={fmtNum(agg.total)} sub={agg.total>0?"-18% vs mois précédent":""} subTone="down" testid="euro-kpi-total"/>
        <KpiCard icon={CheckCircle2} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Taux de conformité"
                 value={agg.total>0?fmtPct(agg.conformes/agg.total,1):"—"} sub={agg.conformes/(agg.total||1)>=0.9?"Cible > 90%":"Sous cible"} subTone="up" testid="euro-kpi-conf"/>
        <KpiCard icon={BarChart3} iconColor="text-blue-600" iconBg="bg-blue-50" label="Note moyenne Eurofins"
                 value={`${(agg.avg*100).toFixed(1).replace(".",",")} / 100`} sub="+3,1 pts vs mois précédent" subTone="up" testid="euro-kpi-avg"/>
        <KpiCard icon={AlertTriangle} iconColor="text-rose-600" iconBg="bg-rose-50" label="Résultats non conformes"
                 value={fmtNum(agg.nonConformes)} sub="vs mois précédent" subTone="down" testid="euro-kpi-nc"/>
        <KpiCard icon={AlertTriangle} iconColor="text-rose-600" iconBg="bg-rose-50" label="KO critiques"
                 value={fmtNum(agg.critiques)} sub="urgent" subTone="down" testid="euro-kpi-crit"/>
        <KpiCard icon={Timer} iconColor="text-slate-600" iconBg="bg-slate-50" label="Délai moyen résultat"
                 value="3,2 jours" sub="Cible ≤ 4 j" subTone="up" testid="euro-kpi-delay"/>
      </div>

      <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-rose-600"/><h3 className="text-[12px] font-bold text-rose-900">Alertes Eurofins</h3></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <AlertPill icon={AlertTriangle} tone="rose" main={`${agg.critiques} KO critiques`} sub="Salmonelle / Listeria"/>
          <AlertPill icon={AlertTriangle} tone="amber" main={`${agg.nonConformes} corners non conformes`} sub="Résultats > seuil"/>
          <AlertPill icon={Timer} tone="amber" main={`${Math.max(0, Math.round(agg.total*0.03))} prélèvements en attente`} sub="Résultats sous 5 jours"/>
          <AlertPill icon={Info} tone="blue" main={`${agg.top5dn.length} corners en recontrôle`} sub="Plan d'action en cours"/>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-bold text-slate-900">Évolution note moyenne Eurofins</h3>
            <div className="flex items-center gap-2 text-[10px]"><span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-600"/>Note moyenne</span></div>
          </div>
          <div className="h-52">
            <ResponsiveContainer>
              <LineChart data={agg.evolution} margin={{top:6,right:12,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="mois" tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <YAxis domain={[0.6,1]} tickFormatter={v=>`${Math.round(v*100)}`} tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <Tooltip formatter={v=>`${(v*100).toFixed(1)} /100`}/>
                <ReferenceLine y={0.9} stroke="#10B981" strokeDasharray="4 4"/>
                <Line type="monotone" dataKey="note" stroke="#8B5CF6" strokeWidth={2.5} dot={{r:3,fill:"#8B5CF6"}} activeDot={{r:5}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Répartition des résultats</h3>
          <div className="h-52 flex items-center">
            <div className="flex-1 h-full relative">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={agg.buckets} innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {agg.buckets.map((b,i)=><Cell key={i} fill={b.color}/>)}
                  </Pie>
                  <Tooltip/>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="text-xl font-bold text-slate-900 tabular-nums">{fmtNum(agg.total)}</div>
                  <div className="text-[10px] text-slate-500">prélèvements</div>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-1.5 pl-2">
              {agg.buckets.map(b => (
                <div key={b.name} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2.5 h-2.5 rounded-full" style={{background:b.color}}/>
                  <span className="flex-1 text-slate-700">{b.name}</span>
                  <span className="tabular-nums font-semibold text-slate-900">{b.value}</span>
                  <span className="text-[9px] text-slate-400 w-10 text-right">{fmtPct(b.value/totalBuckets,1)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Conformité par région</h3>
          <div className="space-y-1.5 max-h-52 overflow-auto">
            {agg.regions.map(r => (
              <div key={r.region} className="flex items-center gap-2 text-[11px]">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{background:REGION_COLORS[r.region?.toUpperCase()]||"#94A3B8"}}/>
                <span className="flex-1 truncate text-slate-700 capitalize">{r.region?.toLowerCase()}</span>
                <span className="tabular-nums font-semibold text-slate-900">{fmtPct(r.note,1)}</span>
                <span className="text-[9px] text-slate-400 w-10 text-right">{r.n} an.</span>
              </div>
            ))}
            {agg.regions.length===0 && <div className="text-[11px] italic text-slate-400 py-2">Aucune donnée</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Top5List title="Top 5 – Meilleure note moyenne" rows={agg.top5up} icon={TrendingUp} iconColor="text-emerald-600" positive testid="euro-top5up"/>
        <Top5List title="Top 5 – Note en baisse" rows={agg.top5dn} icon={TrendingDown} iconColor="text-rose-600" positive={false} testid="euro-top5dn"/>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-purple-600"/><h3 className="text-[13px] font-bold text-slate-900">KO par type d'analyse</h3></div>
          <div className="space-y-1.5">
            {agg.types.map(c => (
              <div key={c.name} className="flex items-center gap-2 text-[11px]">
                <span className="flex-1 truncate text-slate-700">{c.name}</span>
                <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-purple-500" style={{width:`${c.pct*100}%`}}/></div>
                <span className="tabular-nums font-semibold text-slate-900 w-14 text-right">{Math.round(c.pct*100)}% ({c.count})</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4">
          <div className="flex items-center gap-2 mb-2"><OrbitAI className="w-4 h-4 text-purple-600"/><h3 className="text-[13px] font-bold text-slate-900">Analyse IA – Eurofins</h3></div>
          <div className="space-y-2 text-[11px]">
            <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Conformité progresse de +2,4 pts</div><div className="text-slate-500">Proche de l'objectif 90%.</div></div></div>
            <div className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Listeria en hausse</div><div className="text-slate-500">Vigilance sur la chaîne du froid.</div></div></div>
            <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Délai moyen s'améliore</div><div className="text-slate-500">3,2 jours (-0,8 j).</div></div></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <h2 className="text-[14px] font-bold text-slate-900">Derniers prélèvements Eurofins</h2>
          <span className="text-[11px] text-slate-500">{filtered.length} résultats</span>
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un corner…"
                   className="pl-7 pr-6 py-1.5 rounded-md border border-slate-200 text-[11px] focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[220px]"/>
          </div>
        </div>
        <div className="overflow-auto max-h-[420px] scrollbar-thin">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Corner</th>
                <th className="px-3 py-2">Région</th>
                <th className="px-3 py-2">Commentaire / résultat</th>
                <th className="px-3 py-2 text-right">Note</th>
                <th className="px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0,200).map((r,i) => {
                const ok = (r.note||0) >= 0.8;
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 tabular-nums text-slate-700">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900 flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${ok?"bg-emerald-500":"bg-rose-500"}`}/>
                      {r.corner}
                    </td>
                    <td className="px-3 py-2 text-slate-600 capitalize">{r.region?.toLowerCase()||"—"}</td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[300px]" title={r.commentaires}>{r.commentaires||"—"}</td>
                    <td className={`px-3 py-2 text-right font-bold tabular-nums ${ok?"text-emerald-600":"text-rose-600"}`}>{r.note!=null?fmtPct(r.note,1):"—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${ok?"bg-emerald-100 text-emerald-700":"bg-rose-100 text-rose-700"}`}>
                        {ok?<CheckCircle2 className="w-3 h-3"/>:<AlertTriangle className="w-3 h-3"/>}
                        {ok?"Conforme":"Non conforme"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-[12px] italic">Aucun prélèvement Eurofins pour ces filtres.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ TAB : MAGASIN ═══════════════════════ */
function TabMag({ agg, mag, search, setSearch }) {
  const filtered = search ? mag.filter(r => (r.corner||"").toLowerCase().includes(search.toLowerCase())) : mag;
  const totalBuckets = agg.buckets.reduce((s,b)=>s+b.value,0)||1;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={StoreIcon} iconColor="text-blue-600" iconBg="bg-blue-50" label="Audits magasin"
                 value={fmtNum(agg.total)} sub={agg.total>0?"vs mois précédent":""} testid="mag-kpi-total"/>
        <KpiCard icon={CheckCircle2} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Note moyenne"
                 value={agg.avg?fmtNum(agg.avg,0):"—"} sub="Cible ≥ 900 / 1000" testid="mag-kpi-avg"/>
        <KpiCard icon={AlertTriangle} iconColor="text-rose-600" iconBg="bg-rose-50" label="KO totaux"
                 value={fmtNum(agg.koTotal)} sub="à corriger" subTone="down" testid="mag-kpi-ko"/>
        <KpiCard icon={AlertTriangle} iconColor="text-rose-600" iconBg="bg-rose-50" label="Audits critiques"
                 value={fmtNum(agg.critiques)} sub="≥ 3 KO" testid="mag-kpi-crit"/>
        <KpiCard icon={Users} iconColor="text-blue-600" iconBg="bg-blue-50" label="Corners audités"
                 value={fmtNum(agg.corners.length)} sub="couverture réseau" testid="mag-kpi-cov"/>
        <KpiCard icon={Timer} iconColor="text-slate-600" iconBg="bg-slate-50" label="Dernier audit"
                 value={agg.corners[0]?.last_date ? fmtDate(agg.corners.map(c=>c.last_date).sort().reverse()[0]) : "—"} sub="plus récent" testid="mag-kpi-last"/>
      </div>

      <div className="bg-amber-50/40 border border-amber-100 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-600"/><h3 className="text-[12px] font-bold text-amber-900">Alertes &amp; suivi</h3></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <AlertPill icon={AlertTriangle} tone="rose" main={`${agg.critiques} audits critiques`} sub="≥ 3 KO relevés"/>
          <AlertPill icon={Info} tone="blue" main={`${agg.top5dn.length} corners en régression`} sub="Notes en baisse"/>
          <AlertPill icon={Calendar} tone="amber" main={`${agg.corners.filter(c=>{if(!c.last_date)return true;return (Date.now()-new Date(c.last_date).getTime())/86400000 > 45;}).length} audits en retard`} sub="> 45 j sans passage"/>
          <AlertPill icon={CheckCircle2} tone="green" main={`${agg.corners.filter(c=>c.ko===0).length} corners sans KO`} sub="Zéro non-conformité"/>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Évolution KO / audit</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={agg.evolution} margin={{top:6,right:12,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="mois" tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <Tooltip/>
                <Bar dataKey="note" fill="#3B82F6" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Répartition des audits</h3>
          <div className="h-52 flex items-center">
            <div className="flex-1 h-full relative">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={agg.buckets} innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {agg.buckets.map((b,i)=><Cell key={i} fill={b.color}/>)}
                  </Pie>
                  <Tooltip/>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="text-xl font-bold text-slate-900 tabular-nums">{fmtNum(agg.total)}</div>
                  <div className="text-[10px] text-slate-500">audits</div>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-1.5 pl-2">
              {agg.buckets.map(b => (
                <div key={b.name} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2.5 h-2.5 rounded-full" style={{background:b.color}}/>
                  <span className="flex-1 text-slate-700">{b.name}</span>
                  <span className="tabular-nums font-semibold text-slate-900">{b.value}</span>
                  <span className="text-[9px] text-slate-400 w-10 text-right">{fmtPct(b.value/totalBuckets,0)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">KO moyens par région</h3>
          <div className="space-y-1.5 max-h-52 overflow-auto">
            {agg.regions.map(r => (
              <div key={r.region} className="flex items-center gap-2 text-[11px]">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{background:REGION_COLORS[r.region?.toUpperCase()]||"#94A3B8"}}/>
                <span className="flex-1 truncate text-slate-700 capitalize">{r.region?.toLowerCase()}</span>
                <span className="tabular-nums font-semibold text-slate-900">{r.note.toFixed(2)}</span>
                <span className="text-[9px] text-slate-400 w-10 text-right">{r.n} au.</span>
              </div>
            ))}
            {agg.regions.length===0 && <div className="text-[11px] italic text-slate-400 py-2">Aucune donnée</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Top5List title="Top 5 – Corners exemplaires" rows={agg.corners.slice().sort((a,b)=>(a.ko-b.ko)||(b.note-a.note)).slice(0,5).map(c=>({...c,delta:c.delta}))} icon={CheckCircle2} iconColor="text-emerald-600" positive testid="mag-top5up"/>
        <Top5List title="Top 5 – Corners à surveiller" rows={agg.top5dn.map(c=>({...c,note:c.ko,delta:c.delta}))} icon={AlertTriangle} iconColor="text-rose-600" positive={false} testid="mag-top5dn"/>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-blue-600"/><h3 className="text-[13px] font-bold text-slate-900">Non-conformités par catégorie</h3></div>
          <div className="space-y-1.5">
            {agg.categories.map(c => (
              <div key={c.name} className="flex items-center gap-2 text-[11px]">
                <span className="flex-1 truncate text-slate-700">{c.name}</span>
                <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{width:`${c.pct*100}%`}}/></div>
                <span className="tabular-nums font-semibold text-slate-900 w-14 text-right">{Math.round(c.pct*100)}% ({c.count})</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4">
          <div className="flex items-center gap-2 mb-2"><OrbitAI className="w-4 h-4 text-purple-600"/><h3 className="text-[13px] font-bold text-slate-900">Analyse IA – Audits MAG</h3></div>
          <div className="space-y-2 text-[11px]">
            <div className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">{agg.categories[0]?.name} en tête</div><div className="text-slate-500">{agg.categories[0]?.count||0} non-conformités relevées.</div></div></div>
            <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Focus formation</div><div className="text-slate-500">Renforcer les points faibles.</div></div></div>
            <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">{agg.corners.filter(c=>c.ko===0).length} corners sans KO</div><div className="text-slate-500">Excellente rigueur opérationnelle.</div></div></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <h2 className="text-[14px] font-bold text-slate-900">Derniers audits magasin</h2>
          <span className="text-[11px] text-slate-500">{filtered.length} résultats</span>
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un corner…"
                   className="pl-7 pr-6 py-1.5 rounded-md border border-slate-200 text-[11px] focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[220px]"/>
          </div>
        </div>
        <div className="overflow-auto max-h-[420px] scrollbar-thin">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Corner</th>
                <th className="px-3 py-2">DR</th>
                <th className="px-3 py-2">Non-conformité</th>
                <th className="px-3 py-2 text-right">KO</th>
                <th className="px-3 py-2 text-right">Note</th>
                <th className="px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0,200).map((r,i) => {
                const ok = (r.nb_ko||0) === 0;
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 tabular-nums text-slate-700">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900 flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${ok?"bg-emerald-500":"bg-rose-500"}`}/>
                      {r.corner}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.dr||"—"}</td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[300px]" title={r.commentaires_nc}>{r.commentaires_nc||"—"}</td>
                    <td className={`px-3 py-2 text-right font-semibold tabular-nums ${ok?"text-slate-400":"text-rose-600"}`}>{r.nb_ko||0}</td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums text-slate-900">{r.note?fmtNum(r.note,0):"—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${ok?"bg-emerald-100 text-emerald-700":"bg-rose-100 text-rose-700"}`}>
                        {ok?<CheckCircle2 className="w-3 h-3"/>:<AlertTriangle className="w-3 h-3"/>}
                        {ok?"Conforme":"Non conforme"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-[12px] italic">Aucun audit magasin pour ces filtres.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
